import { Tooltip as TooltipPrimitive } from 'radix-ui';
import * as React from 'react';

import { getTooltipPlacement } from './Tooltip.helpers';
import { $TooltipArrow, $TooltipContent } from './Tooltip.styles';
import {
  TOOLTIP_POSITION,
  TOOLTIP_VARIANT,
  TooltipPosition,
  TooltipVariant,
} from './Tooltip.types';

export function TooltipProvider({
  delayDuration = 200,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

type TooltipContentProps = React.ComponentProps<
  typeof TooltipPrimitive.Content
> & {
  variant?: TooltipVariant;
};

function TooltipContent({
  sideOffset = 0,
  children,
  variant = 'black',
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <$TooltipContent
        data-slot="tooltip-content"
        data-variant={variant}
        sideOffset={sideOffset}
        {...props}
      >
        {children}
        <$TooltipArrow data-variant={variant} />
      </$TooltipContent>
    </TooltipPrimitive.Portal>
  );
}

type WrappedTooltipProps = Omit<
  React.ComponentProps<typeof TooltipPrimitive.Content>,
  'children' | 'content' | 'side' | 'align'
> & {
  content?: React.ReactNode;
  children: React.ReactElement;
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  position?: TooltipPosition;
  variant?: TooltipVariant;
};

const WrappedTooltip: React.FC<WrappedTooltipProps> = ({
  content,
  children,
  isOpen,
  defaultIsOpen,
  onOpenChange,
  position = TOOLTIP_POSITION.TOP,
  variant = TOOLTIP_VARIANT.BLACK,
  ...props
}) => {
  const { side, align } = getTooltipPlacement(position);

  return (
    <Tooltip
      open={isOpen}
      defaultOpen={defaultIsOpen}
      onOpenChange={onOpenChange}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align} variant={variant} {...props}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
};

export default WrappedTooltip;
